import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faPlus } from "@fortawesome/pro-regular-svg-icons";
import {
  GetEmailMarketingTemplates,
  PostEmailMarketingTemplateDuplicate,
} from "api/rpc/2024-04/facilityAdmin/marketing/emailMarketingTemplates";
import type { IEmailMarketingTemplateListItem } from "api/rpc/2024-04/facilityAdmin/marketing/emailMarketingTemplatesTypes";
import { ButtonNew } from "components/buttonNew";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { emailTemplateBuilderPath } from "../emailBuilderWorkspace";
import { EMAIL_BUILDER_FACILITY_TEMPLATES_LIMIT } from "../emailBuilderConstants";
import { EmailBuilderTemplateLibraryCard } from "./EmailBuilderTemplateLibraryCard";
import { EmailBuilderTemplateLibraryDeleteModal } from "./EmailBuilderTemplateLibraryDeleteModal";
import {
  EmailBuilderTemplateLibraryNameModal,
  EmailBuilderTemplateLibraryRenameModal,
} from "./EmailBuilderTemplateLibraryNameModal";
import "../templatePicker/emailBuilderTemplatePicker.scss";
import "./emailBuilderTemplateLibrary.scss";
import { StatusCode } from "api/protocols";

type TLibraryLoadState =
  | { status: "loading" }
  | { status: "failed"; message: string }
  | { status: "succeeded"; facility: IEmailMarketingTemplateListItem[] };

type TLibraryDialogState =
  | { kind: "none" }
  | { kind: "rename"; template: IEmailMarketingTemplateListItem }
  | { kind: "delete"; template: IEmailMarketingTemplateListItem };

export default function EmailBuilderTemplateLibrary() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const requestIdRef = useRef(0);
  const actionLockRef = useRef(false);
  const [offset, setOffset] = useState(0);
  const [loadState, setLoadState] = useState<TLibraryLoadState>({ status: "loading" });
  const [busyTemplateId, setBusyTemplateId] = useState<number | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [dialogState, setDialogState] = useState<TLibraryDialogState>({ kind: "none" });

  const loadTemplates = useCallback(async (nextOffset: number) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoadState({ status: "loading" });

    const templatesRes = await GetEmailMarketingTemplates(
      {
        limit: EMAIL_BUILDER_FACILITY_TEMPLATES_LIMIT,
        offset: nextOffset,
      },
      false,
    );

    if (requestId !== requestIdRef.current) {
      return;
    }

    if (templatesRes.status !== StatusCode.OK || templatesRes.data == null) {
      setLoadState({
        status: "failed",
        message:
          typeof templatesRes.message === "string" && templatesRes.message.length > 0
            ? templatesRes.message
            : t("Could not load email templates."),
      });
      return;
    }

    if (templatesRes.data.facility.length === 0 && nextOffset > 0) {
      setOffset(Math.max(0, nextOffset - EMAIL_BUILDER_FACILITY_TEMPLATES_LIMIT));
      return;
    }

    setLoadState({
      status: "succeeded",
      facility: templatesRes.data.facility,
    });
  }, [t]);

  useEffect(() => {
    void loadTemplates(offset);

    return () => {
      requestIdRef.current += 1;
    };
  }, [loadTemplates, offset]);

  function handleEdit(templateId: number) {
    if (actionLockRef.current) {
      return;
    }

    history.push(emailTemplateBuilderPath(templateId));
  }

  async function handleDuplicate(template: IEmailMarketingTemplateListItem) {
    if (actionLockRef.current) {
      return;
    }

    actionLockRef.current = true;
    setBusyTemplateId(template.id);

    try {
      const res = await PostEmailMarketingTemplateDuplicate(template.id, false);
      if (res.status !== StatusCode.OK || res.data?.data == null) {
        dispatch(
          showError(
            typeof res.message === "string" && res.message.length > 0
              ? res.message
              : t("Could not duplicate template."),
          ),
        );
        return;
      }

      dispatch(showSuccess(t("Template duplicated")));
      if (offset === 0) {
        await loadTemplates(0);
      } else {
        setOffset(0);
      }
    } finally {
      actionLockRef.current = false;
      setBusyTemplateId(null);
    }
  }

  function updateFacilityTemplate(updated: { id: number; name: string }) {
    setLoadState(current => {
      if (current.status !== "succeeded") {
        return current;
      }

      return {
        status: "succeeded",
        facility: current.facility.map(item =>
          item.id === updated.id ? { ...item, name: updated.name } : item,
        ),
      };
    });
  }

  function handleTableOffset(direction: "prev" | "next") {
    setOffset(prev =>
      direction === "prev"
        ? Math.max(0, prev - EMAIL_BUILDER_FACILITY_TEMPLATES_LIMIT)
        : prev + EMAIL_BUILDER_FACILITY_TEMPLATES_LIMIT,
    );
  }

  const showPagination =
    loadState.status === "succeeded" &&
    (offset > 0 || loadState.facility.length === EMAIL_BUILDER_FACILITY_TEMPLATES_LIMIT);

  return (
    <>
      <div className="email-builder-template-picker" data-email-builder-template-library="true">
        {loadState.status === "loading" ? (
          <div
            className="email-builder-template-picker__skeleton-grid"
            aria-busy="true"
            aria-live="polite"
          >
            {[0, 1, 2].map(index => (
              <div key={index} className="email-builder-template-picker__skeleton-card">
                <div className="email-builder-template-picker__skeleton-preview" />
                <div className="email-builder-template-picker__skeleton-title" />
              </div>
            ))}
          </div>
        ) : null}

        {loadState.status === "failed" ? (
          <div className="email-builder-template-picker__error" role="alert">
            <p className="email-builder-template-picker__error-text">{loadState.message}</p>
            <ButtonNew type="secondary" onClick={() => void loadTemplates(offset)}>
              {t("Retry")}
            </ButtonNew>
          </div>
        ) : null}

        {loadState.status === "succeeded" ? (
          loadState.facility.length === 0 && offset === 0 ? (
            <MessageDisplay
              centered
              icon="table-layout"
              title={t("No saved templates yet")}
              content={t(
                "Create a blank template or save a campaign as a template to reuse it here.",
              )}
              actions={[
                {
                  label: t("Create template"),
                  action: () => setCreateModalOpen(true),
                  type: "primary",
                  disabled: busyTemplateId != null,
                },
              ]}
            />
          ) : (
          <section
            className="email-builder-template-picker__section"
            aria-labelledby="email-template-library-my-templates-heading"
          >
            <div className="pb-6 flex justify-between items-end gap-4">
              <div className="space-y-2">
                <div
                  id="email-template-library-my-templates-heading"
                  className="font-medium"
                >
                  {t("My templates")}
                </div>
                <p>{t("Manage reusable layouts for your facility email campaigns.")}</p>
              </div>
              <ButtonNew
                size="medium"
                type="primary"
                disabled={busyTemplateId != null}
                onClick={() => setCreateModalOpen(true)}
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" aria-hidden="true" />
                {t("Create template")}
              </ButtonNew>
            </div>

            <div className="email-builder-template-picker__grid">
              {loadState.facility.map(template => (
                <EmailBuilderTemplateLibraryCard
                  key={template.id}
                  template={template}
                  disabled={busyTemplateId != null}
                  onEdit={handleEdit}
                  onRename={item => setDialogState({ kind: "rename", template: item })}
                  onDuplicate={item => void handleDuplicate(item)}
                  onDelete={item => setDialogState({ kind: "delete", template: item })}
                />
              ))}
            </div>

            {showPagination ? (
              <div className="email-builder-template-picker__pagination">
                <ButtonNew
                  type="secondary"
                  size="small"
                  disabled={offset <= 0}
                  onClick={() => handleTableOffset("prev")}
                >
                  <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
                  &nbsp;
                  {t("Previous")}
                </ButtonNew>
                <ButtonNew
                  type="secondary"
                  size="small"
                  disabled={loadState.facility.length < EMAIL_BUILDER_FACILITY_TEMPLATES_LIMIT}
                  onClick={() => handleTableOffset("next")}
                >
                  {t("Next")}
                  &nbsp;
                  <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                </ButtonNew>
              </div>
            ) : null}
          </section>
          )
        ) : null}
      </div>

      <EmailBuilderTemplateLibraryNameModal
        mode="create"
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={templateId => {
          dispatch(showSuccess(t("Email template created successfully")));
          history.push(emailTemplateBuilderPath(templateId));
        }}
      />

      <EmailBuilderTemplateLibraryRenameModal
        open={dialogState.kind === "rename"}
        template={dialogState.kind === "rename" ? dialogState.template : null}
        onClose={() => setDialogState({ kind: "none" })}
        onRenamed={updated => {
          updateFacilityTemplate(updated);
          dispatch(showSuccess(t("Template renamed")));
        }}
      />

      <EmailBuilderTemplateLibraryDeleteModal
        open={dialogState.kind === "delete"}
        template={dialogState.kind === "delete" ? dialogState.template : null}
        onClose={() => setDialogState({ kind: "none" })}
        onDeleted={() => {
          dispatch(showSuccess(t("Template deleted")));
          void loadTemplates(offset);
        }}
        onError={message => dispatch(showError(message))}
      />
    </>
  );
}
